import {
  HOUSE_ABOUT_STEP,
  HOUSE_FIND_STEP,
} from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/House";
import {
  HousePriceFormValues,
  HouseProductDetailsFormValues,
  HouseProductIntroFormValues,
} from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/House/houseFormValuesInterfaces";
import {
  HOUSE_CONTENTS_ABOUT_STEP,
  HOUSE_CONTENTS_INTRO_STEP,
  HOUSE_CONTENTS_PRICE_STEP,
} from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/HouseContents";
import {
  HouseContentsProductDetailsFormValues,
  HouseContentsProductIntroFormValues,
} from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/HouseContents/houseContentsFormValuesInterfaces";

import { DamageRecentYears } from "Components/Form/RecentDamages/recentDamagesInterfaces";
import { isTestOrDevelopmentEnv } from "Store/featureToggles/featureToggleUtils";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { mapFormToInjuries } from "Store/priceCalculation/productMappers/common/injuriesMapper";
import { nullableFormBooleanToNullableBoolean } from "Utils/formUtils";
import { isDefined } from "Utils/functions";

export type HouseContentsProductBackend = {
  name: INSURANCE_NAME_VALUE;
  coverageGroup: any;
  addressSameAsHomeAddress: boolean;
  address?: {
    zipCode: string;
    city?: string;
  };
  fireResistantBuildingMaterial: boolean;
  coverageAmount: number;
  damageCountRecentYears: number;
  yearOfLastDamage?: number | null;
  hasFireAlarm?: boolean;
  hasBurglarAlarm?: boolean;
  fullRental?: boolean;
  streetAddress?: string;
};

const getInjuries = (
  checkPrice: HousePriceFormValues,
  aboutHouseContents:
    | HouseContentsProductDetailsFormValues
    | HouseProductDetailsFormValues,
  isInHouse: boolean,
): number => {
  if (isInHouse) {
    return checkPrice.injuries ? Number(checkPrice.injuries) : 0;
  }
  return Number(aboutHouseContents.injuries);
};

const getInjuryYear = (
  checkPrice: HousePriceFormValues,
  aboutHouseContents:
    | HouseContentsProductDetailsFormValues
    | HouseProductDetailsFormValues,
  isInHouse: boolean,
): number | null => {
  if (isInHouse) {
    return checkPrice.injuryYear ? Number(checkPrice.injuryYear) : null;
  }
  return aboutHouseContents.injuryYear
    ? Number(aboutHouseContents.injuryYear)
    : null;
};

export function mapToCommerceHouseContents(data: {
  [key: string]: any;
}): HouseContentsProductBackend {
  const introHouseContents =
    data.formData[HOUSE_CONTENTS_INTRO_STEP.id] ??
    data.formData[HOUSE_FIND_STEP.id];

  const aboutHouseContents =
    data.formData[HOUSE_CONTENTS_ABOUT_STEP.id] ??
    data.formData[HOUSE_ABOUT_STEP.id];
  const checkPrice = data.formData[HOUSE_CONTENTS_PRICE_STEP.id];

  const isInHouse = isDefined(data.formData[HOUSE_ABOUT_STEP.id]);

  const injuryYear = getInjuryYear(checkPrice, aboutHouseContents, isInHouse);
  const damageCountRecentYears = getInjuries(
    checkPrice,
    aboutHouseContents,
    isInHouse,
  );
  return {
    name: INSURANCE_NAME_VALUE.HOUSE_CONTENTS,
    coverageGroup: data.coverage,
    addressSameAsHomeAddress:
      nullableFormBooleanToNullableBoolean(
        introHouseContents.addressSameAsHomeAddress,
      ) ?? false,
    address:
      introHouseContents.city && introHouseContents.postNumber
        ? {
            city: introHouseContents.city,
            zipCode: introHouseContents.postNumber,
          }
        : undefined,
    fireResistantBuildingMaterial: isInHouse
      ? checkPrice.fireResistantBuildingMaterial
      : nullableFormBooleanToNullableBoolean(
          aboutHouseContents.fireResistantBuildingMaterial,
        ) ?? false,
    fullRental: isInHouse
      ? checkPrice.fullRental
      : nullableFormBooleanToNullableBoolean(introHouseContents.fullRental),
    hasFireAlarm: checkPrice.hasFireAlarm,
    hasBurglarAlarm: checkPrice.hasBurglarAlarm,
    coverageAmount: isInHouse
      ? checkPrice.contentsInsuranceSum
      : aboutHouseContents.coverageAmount,
    ...mapFormToInjuries(
      String(damageCountRecentYears) as DamageRecentYears,
      injuryYear ? String(injuryYear) : undefined,
    ),
    streetAddress: getStreetAddress(introHouseContents),
  };
}

const getStreetAddress = (
  introFormValues:
    | HouseProductIntroFormValues
    | HouseContentsProductIntroFormValues,
): string | undefined => {
  if (
    "realEstateInfo" in introFormValues &&
    isDefined(introFormValues.address)
  ) {
    return isTestOrDevelopmentEnv()
      ? TEST_STREET_ADDRESS
      : introFormValues.address;
  }
};

const TEST_STREET_ADDRESS = "Test address 1A";
